import { syncEngine } from './syncEngine'
import { canSyncRows, type RemoteSettings } from './syncedRows'
import { useCalendarStore } from '../../stores/calendar'
import { useWordCountStore } from '../../stores/wordCounts'
import { useWritingLogStore } from '../../stores/writingLog'
import { useGoalsStore } from '../../stores/goals'
import { useGoalLogStore } from '../../stores/goalLog'
import { useDocketStore } from '../../stores/docket'
import { useSprintStore } from '../../stores/sprints'
import { useLooseEndsStore } from '../../stores/looseEnds'
import { useRevisionStore } from '../../stores/revision'
import { useSettingsStore } from '../../stores/settings'
import { useCorpusStore } from '../../stores/corpus'
import { syncPrepTasks } from '../tools/prepTasks'

// Prep tasks are worked out from events and Docket together, so they wait
// until this device has caught up with the cloud at least once (or isn't
// syncing at all), and never run halfway through a pull. Otherwise a device
// that hasn't yet received a prep task could make a fresh one over it.
let pulling = 0
let caughtUp = false

/** Whether events' prep tasks may be brought in line now (see syncPrepTasks). */
export function prepTasksMayRun(): boolean {
  return pulling === 0 && (caughtUp || !canSyncRows())
}

/** Applies the tools' rows from a settings pull, then sends local changes. */
export async function syncToolData(remote: RemoteSettings) {
  pulling += 1
  try {
    await applyToolRows(remote)
  } finally {
    pulling -= 1
  }
  caughtUp = true
  // Events changed elsewhere may move their prep tasks. That is tidying, so a
  // fault in it never holds up the pull.
  try {
    syncPrepTasks()
  } catch (err) {
    console.warn('[ToolDataSync] Prep tasks not updated:', err)
  }
}

function applyToolRows(remote: RemoteSettings) {
  return Promise.all([
    useCalendarStore().syncWithRemote(remote),
    useWordCountStore().syncWithRemote(remote),
    useWritingLogStore().syncWithRemote(remote),
    useGoalsStore().syncWithRemote(remote),
    useGoalLogStore().syncWithRemote(remote),
    useDocketStore().syncWithRemote(remote),
    useSprintStore().syncWithRemote(remote),
    useLooseEndsStore().syncWithRemote(remote),
    useRevisionStore().syncWithRemote(remote),
    useCorpusStore().syncLastOpenedPage(remote),
  ])
}

/**
 * Delivers one settings pull to the settings store and every tool, then
 * records it as applied so the next pull asks only for newer rows. They share
 * one cursor, so it only moves when all of them have taken the pull.
 */
export async function applySettingsPull(remote: RemoteSettings) {
  const settings = useSettingsStore()
  let applied = true
  if (settings.syncSettingsEnabled) {
    applied = await settings.syncSettingsWithCloud(remote ?? {})
  } else if (remote && Object.keys(remote).length > 0) {
    settings.applyRemoteSettings(remote)
  }
  await syncToolData(remote)
  if (applied) syncEngine.commitSettingsPull(remote)
}

/** Pulls what changed and catches every tool up, e.g. when a tool comes on screen. */
export async function refreshToolData() {
  if (!canSyncRows()) return
  try {
    await applySettingsPull(await syncEngine.pullSettings())
  } catch (err) {
    console.warn('[ToolDataSync] Refresh failed:', err)
  }
}
